"use client";
import React, { useState, useRef } from "react";
import SubmissionSuccessCard from "./SubmissionSuccessCard"; // 🌟 Import your new success card

// ---------------------------------------------------------------------------
// Main Form
// ---------------------------------------------------------------------------
export default function ThankyouForm() {
  const initialFormState = {
    name: "",
    grade:     "",
    schoollocation:   "", // Ensure this casing matches your input element names exactly
    thankyou: ""
  };

  const [formData, setFormData] = useState(initialFormState);
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [isVerified, setIsVerified] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // 🌟 NEW STATES FOR CARD RENDERING
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let imageUrl = null;

    try {
      // STEP 1: If there is a file, get a presigned URL and upload to S3
      if (selectedFile) {
        const presignRes = await fetch("/api/upload-url", {
          method: "POST",
          body: JSON.stringify({ filename: selectedFile.name, contentType: selectedFile.type }),
        });
        const { url, uploadUrl } = await presignRes.json();

        // Upload directly to S3
        await fetch(uploadUrl, {
          method: "PUT",
          body: selectedFile,
          headers: { "Content-Type": selectedFile.type },
        });
        
        imageUrl = url; // This is the public link we save to the DB
      }

      // STEP 2: Submit form data + image URL to your existing API
      const response = await fetch("/api/thankyou", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, imageUrl }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // 🌟 CAPTURE PAYLOAD AND SHOW CARD
        // If your API returns success parameters, store them; otherwise create a default layout container object:
        setSubmissionResult({
          success: true,
          title: result?.title || "Thank You Posted! ❤️",
          message: result?.message || "Your message of gratitude has been successfully submitted and saved to our database."
        });
        setIsSubmitted(true);
      } else {
        alert("Submission failed. Please check connection and try again.");
      }
    } catch (error) {
      console.error("Upload failed", error);
    }
  };

  // 🌟 RESET FLOW FOR MULTIPLE ENTRIES
  const handleResetForm = () => {
    setFormData(initialFormState);
    setSelectedFile(null);
    setPreview(null);
    setSubmissionResult(null);
    setIsSubmitted(false);
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file); 
      setPreview(URL.createObjectURL(file));
    }
  };
    
  // 🌟 INTERCEPT VIEW RENDERING
  if (isSubmitted) {
    return (
      <div className="w-full flex justify-center py-20 px-4 bg-orange-50/50">
        <SubmissionSuccessCard 
          responseData={submissionResult} 
          onResetForm={handleResetForm} 
        />
      </div>
    );
  }

  return (
    <>
      {/* Form */}
      <div className="w-full flex justify-center py-20 px-4 bg-orange-50/50">
        <form onSubmit={handleSubmit} className="w-[480px]">
          <div className="bg-white p-10 pb-16 rounded-[2.5rem] shadow-2xl border-t-8 border-sky-400 relative rotate-1 hover:rotate-0 transition-transform duration-500 ease-in-out">

            {/* Decorative icon */}
            <div className="absolute -top-6 -right-6 text-5xl bg-white rounded-full p-2 shadow-lg border border-slate-100">
              ❤️
            </div>

            {/* Header */}
            <h2 className="text-3xl font-bold text-sky-500 mb-10 flex items-center gap-2">
              Post a thank you
            </h2>

            <div className="space-y-6">

              {/* Name */}
              <div className="grid grid-cols-1 gap-4">
                <input
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="name" placeholder="Name"
                  value={formData.name}
                  onChange={handleChange} required
                />
              </div>

              {/* Grade and Location */}
              <div className="grid grid-cols-2 gap-4">
                <input
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="grade" type="text" placeholder="Grade (1-12) 🎓"
                  value={formData.grade}
                  onChange={handleChange}
                />
                <input
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="schoollocation" type="text" placeholder="School or location"
                  value={formData.schoollocation}
                  onChange={handleChange}
                />
              </div>

              {/* Text Area Note */}
              <div className="grid grid-cols-1 gap-4">
                <textarea
                  className="p-4 rounded-2xl border-2 border-slate-100 focus:border-amber-400 outline-none transition-all placeholder:text-slate-300 text-sm bg-slate-50"
                  name="thankyou" placeholder="Write a thank you message"
                  value={formData.thankyou}
                  onChange={handleChange} required
                  rows={4}
                />
              </div>

              <div className="flex flex-col items-center gap-4">
                {/* Hidden Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />

                {/* Styled Upload Toggle Button */}
                <button
                  type="button"
                  onClick={handleButtonClick}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold py-3 px-6 rounded-2xl border-2 border-dashed border-slate-300 transition-all flex items-center gap-2"
                >
                  <span>📸</span>
                  {preview ? "Change Photo" : "Upload Image"}
                </button>

                {/* Upload Image Preview Box */}
                {preview && (
                  <div className="relative w-32 h-32">
                    <img 
                      src={preview} 
                      alt="Preview" 
                      className="w-full h-full object-cover rounded-xl border-2 border-amber-400" 
                    />
                    <button 
                      type="button"
                      onClick={() => { setPreview(null); setSelectedFile(null); }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* Submit Button Trigger */}
              <button
                type="submit"
                disabled={!isVerified} 
                className={`w-full mt-6 font-bold py-5 rounded-2xl transition-all text-xl 
                  ${isVerified 
                    ? "bg-amber-400 hover:bg-amber-500 text-amber-950 shadow-[0_5px_0_0_#ca8a04] active:shadow-none active:translate-y-1" 
                    : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
              >
                Submit Note
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}